import featureFlagService, { featureKeys } from 'services/FeatureFlagService';
import { fetchFromRTM, subscribeToRTM } from './index';
import * as constants from './constants';
import './typedef';

class ProjectResolver {
  constructor(connection) {
    this.connection = connection;
  }
  /**
   * Retrieves projects list. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-project-list/)
   * @param {Object} payload
   * @param {string} payload.workspaceId
   * @returns {Promise.<[ProjectDTO]>}
   */
  workspaceProjectsList = async ({ workspaceId }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_LIST,
      workspaceId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_LIST_OK,
      constants.RTM_WORKSPACE_PROJECT_LIST_ERROR,
    );
    return response.projects;
  };

  /**
   * Retrieves project details. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-project-get/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @returns {Promise.<ProjectDTO>}
   */
  workspaceProjectGet = async ({ projectId }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_GET,
      projectId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_GET_OK,
      constants.RTM_WORKSPACE_PROJECT_GET_ERROR
    );
    return response?.project;
  };

  /**
   * Retrieves a list of activities from a project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-activity-list/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @param {array} payload.fieldNames (properties from config)
   * @returns {Promise.<[ActivitySummary]>}
   */
  projectActivityList = async ({ projectId, fieldNames, publicationStatus }) => {
    const message = {
      type: constants.RTM_PROJECT_ACTIVITY_LIST,
      projectId,
      fieldNames,
      publicationStatus,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_ACTIVITY_LIST_OK,
      constants.RTM_PROJECT_ACTIVITY_LIST_ERROR
    );
    return response.activities;
  };

  /**
   * Retrieves a list of completed exports from a project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-export-list/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @returns {Promise.<[ExportSummaries]>}
   */
  projectExportList = async ({ projectId }) => {
    const message = {
      type: constants.RTM_PROJECT_EXPORT_LIST,
      projectId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_EXPORT_LIST_OK,
      constants.RTM_PROJECT_EXPORT_LIST_ERROR
    );
    return response.exportSummaries;
  };

  /**
   * Fetches a summary list of collaborators belonging to a project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-collaborator-summary/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @returns {Promise.<CollaboratorDto>}
   */
  workspaceProjectCollaboratorSummary = async ({ projectId }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_COLLABORATOR_SUMMARY,
      projectId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_COLLABORATOR_SUMMARY_OK,
      constants.RTM_WORKSPACE_PROJECT_COLLABORATOR_SUMMARY_ERROR
    );
    return response.collaborators;
  };

  /**
   * Allows the user to grant permission to an account over a project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-permission-grant/)
   * @param {Object} payload
   * @param {array} payload.accountOrTeamIds
   * @param {string} payload.projectId
   * @param {string} payload.permissionLevel
   * @returns {Promise.<projectId: string, permissionLevel: string>}
   */
  workspaceProjectPermissionGrant = async ({
    accountOrTeamIds,
    projectId,
    collaboratorType,
    permissionLevel,
  }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_PERMISSION_GRANT,
      [`${collaboratorType}Ids`]: [accountOrTeamIds],
      projectId,
      permissionLevel,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_PERMISSION_GRANT_OK,
      constants.RTM_WORKSPACE_PROJECT_PERMISSION_GRANT_ERROR
    );
    return response;
  };

  /**
   * Allows a project user to revoke project permission of an account. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-permission-revoke/)
   * @param {Object} payload
   * @param {string} payload.accountOrTeamId
   * @param {string} payload.collaboratorType
   * @param {string} payload.projectId
   * @returns {Promise.<void>}
   */
  workspaceProjectPermissionRevoke = async ({
    accountOrTeamId,
    collaboratorType,
    projectId,
  }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_PERMISSION_REVOKE,
      [`${collaboratorType}Id`]: accountOrTeamId,
      projectId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_PERMISSION_REVOKE_OK,
      constants.RTM_WORKSPACE_PROJECT_PERMISSION_REVOKE_ERROR
    );
    return response;
  };

  /**
   * Replace the name or config for a project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-project-replace/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @param {array} payload.name
   * @param {object} payload.config
   * @returns {Promise.<projectId: string>}
   */
  workspaceProjectReplace = async ({ projectId, name, config }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_REPLACE,
      projectId,
      name,
      config,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_REPLACE_OK,
      constants.RTM_WORKSPACE_PROJECT_REPLACE_ERROR
    );
    return response;
  };

  /**
   * Delete a project from a workspace. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-project-delete/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @returns {Promise.<void>}
   */
  workspaceProjectDelete = async ({ projectId }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_DELETE,
      projectId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_DELETE_OK,
      constants.RTM_WORKSPACE_PROJECT_DELETE_ERROR,
    );
    return response;
  };

  /**
   * Creates a new deployment by publishing an activity. // todo: Add rtm docs link
   *
   * @param {Object} payload
   * @param {string} payload.activityId
   * @param {string} payload.cohortId
   * @param {string} payload.deploymentId
   * @returns {Promise.<{id: string, activityId: string, cohortId: string}>}
   */
  projectActivityPublish = async ({ activityId, cohortId, deploymentId }) => {
    const message = {
      type: constants.RTM_PROJECT_ACTIVITY_PUBLISH,
      activityId,
      cohortId,
      deploymentId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_ACTIVITY_PUBLISH_OK,
      constants.RTM_PROJECT_ACTIVITY_PUBLISH_ERROR
    );

    return response.deployment;
  };

  /**
   * Move a project to another workspace
   * @param {Object} payload
   * @param {string} payload.projectId
   * @param {string} payload.workspaceId
   * @returns {Promise.<void>}
   */
  workspaceProjectMove = async ({ projectId, workspaceId }) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_MOVE,
      projectId,
      workspaceId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_MOVE_OK,
      constants.RTM_WORKSPACE_PROJECT_MOVE_ERROR,
    );
    return response;
  };

  /**
   * Duplicates an activity in the project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-activity-duplicate/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @param {string} payload.activityId
   * @param {boolean} newDuplicateFlow
   * @param {string} [targetLanguage]
   * @returns {Promise.<void>}
   */
  projectActivityDuplicate = async (projectId, activityId, targetLanguage) => {
    const isNewDuplicateFlow = featureFlagService.is(featureKeys.PROJECT_ACTIVITY_DUPLICATE_DEEPCOPY);
    const message = {
      type: constants.RTM_PROJECT_ACTIVITY_DUPLICATE,
      activityId,
      projectId,
      newDuplicateFlow: isNewDuplicateFlow,
      targetLanguage,
    };
    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_ACTIVITY_DUPLICATE_OK,
      constants.RTM_PROJECT_ACTIVITY_DUPLICATE_ERROR,
    );
    return response?.activity;
  };

  projectActivityMigrate = async (workspaceId, projectId, activityId) => {
    const message = {
      type: constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST,
      elementId: activityId,
      elementType: "ACTIVITY",
      metadata: `{"targetWorkspaceId":"${workspaceId}", "targetProjectId":"${projectId}"}`,
      exportType: constants.MIGRATE_ETEXT_TO_BV,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_OK,
      constants.RTM_AUTHOR_ELEMENT_EXPORT_REQUEST_ERROR
    );
    return response;
  };  

  /**
   * Delete an activity from a project. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-activity-delete/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @param {string} payload.workspaceId
   * @returns {Promise.<void>}
   */
  workspaceProjectActivityDelete = async ({ projectId, activityId }) => {
    const message = {
      type: constants.RTM_PROJECT_ACTIVITY_DELETE,
      projectId,
      activityId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_PROJECT_ACTIVITY_DELETE_OK,
      constants.RTM_PROJECT_ACTIVITY_DELETE_ERROR,
    );
    return response;
  };

  /**
   * Get project details. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/author-courseware-project-find/)
   * @param {Object} payload
   * @param {string} payload.elementId
   * @param {string} payload.elementType
   * @returns {Promise.<ProjectDTO>}
   */
  authorCoursewareProjectFind = async ({ elementId, elementType }) => {
    const message = {
      type: constants.RTM_AUTHOR_COURSEWARE_PROJECT_FIND,
      elementId,
      elementType,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_AUTHOR_COURSEWARE_PROJECT_FIND_OK,
      constants.RTM_AUTHOR_COURSEWARE_PROJECT_FIND_ERROR,
      null,
      true,
    );
    return response;
  };

  /**
   * Create a new request for the ingestion process. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-ingest-create/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @param {string} payload.configFields
   * @param {string} payload.fileName
   * @param {string} payload.courseName
   * @param {string} payload.workspaceId
   * @param {string} payload.rootElementId optional
   * @returns {Promise.<void>}
   */
    projectIngestCreate = async ({ projectId, configFields, fileName, courseName, workspaceId, rootElementId }) => {
      const message = {
        type: constants.RTM_PROJECT_INGEST_CREATE,
        projectId,
        configFields,
        fileName,
        courseName,
        workspaceId,
        ...(rootElementId && rootElementId.length ? {rootElementId} : {}),
      };

      const response = await fetchFromRTM(
        this.connection,
        message,
        constants.RTM_PROJECT_INGEST_CREATE_OK,
        constants.RTM_PROJECT_INGEST_CREATE_ERROR,
        '',
        true
      );
      return response;
    };

    /**
   * Creates a subscription to ingestions at project level. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-project-subscribe/)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @param {function} listener The callback to the subscription.
   * @returns {Promise.<void>}
   */

  workspaceProjectSubscribe = async (projectId, listener) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_SUBSCRIBE,
      projectId,
    };

    const subscription = await subscribeToRTM(
      this.connection,
      message,
      listener,
    );

    if (!subscription) {
      const unsubscribeFn = () => {};
      return unsubscribeFn;
    }

    const unsubscribeMessage = {
      type: constants.RTM_WORKSPACE_PROJECT_UNSUBSCRIBE,
      projectId,
    };

    const unsubscribeFn = () => subscription.unsubscribe(unsubscribeMessage);
    return unsubscribeFn;
  };

  /**
   * Unsubscribe from project ingestions. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/workspace-project-unsubscribe)
   * @param {Object} payload
   * @param {string} payload.projectId
   * @returns {Promise.<void>}
   */
  workspaceProjectUnsubscribe = async (projectId) => {
    const message = {
      type: constants.RTM_WORKSPACE_PROJECT_UNSUBSCRIBE,
      projectId,
    };

    const response = await fetchFromRTM(
      this.connection,
      message,
      constants.RTM_WORKSPACE_PROJECT_UNSUBSCRIBE_OK,
      constants.RTM_WORKSPACE_PROJECT_UNSUBSCRIBE_ERROR,
      '',
    );
    return response;
  };
  /*
   * Gets the project ingestion logs. See
   *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-ingest-log-list)
   * @param {Object} payload
   * @param {string} payload.ingestionId
   * @returns {Promise.<void>}
   */
    projectIngestLog = async ({ ingestionId }) => {
      const message = {
        type: constants.RTM_PROJECT_INGEST_LOG_LIST,
        ingestionId,
      };

      const response = await fetchFromRTM(
        this.connection,
        message,
        constants.RTM_PROJECT_INGEST_LOG_LIST_OK,
        constants.RTM_PROJECT_INGEST_LOG_LIST_ERROR,
        ''
      );
      return response;
    };

    /**
     * Start a new ingestion process. See
     *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-ingest-start/)
     * @param {string} payload.ingestionId
     * @returns {Promise.<void>}
     */
    projectIngestStart = async ({ ingestionId, adapterType}) => {
      const message = {
        type: constants.RTM_PROJECT_INGEST_START,
        ingestionId,
        adapterType,
      };

      const response = await fetchFromRTM(
        this.connection,
        message,
        constants.RTM_PROJECT_INGEST_START_OK,
        constants.RTM_PROJECT_INGEST_START_ERROR,
        ''
      );
      return response;
    };

    /**
     * Update existing ingestion. See
     *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-ingest-update/)
     * @param {string} payload.ingestionId
     * @param {string} payload.projectId
     * @param {string} payload.status
     * @returns {Promise.<void>}
     */
    projectIngestUpdate = async ({ ingestionId, projectId, status }) => {
      const message = {
        type: constants.RTM_PROJECT_INGEST_UPDATE,
        ingestionId,
        projectId,
        status,
      };

      const response = await fetchFromRTM(
        this.connection,
        message,
        constants.RTM_PROJECT_INGEST_UPDATE_OK,
        constants.RTM_PROJECT_INGEST_UPDATE_ERROR,
        ''
      );
      return response;
    };

    /**
     * Delete existing ingestion. See
     *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-ingest-delete/)
     * @param {string} payload.ingestionId
     * @returns {Promise.<void>}
     */
    projectIngestDelete = async ({ ingestionId }) => {
      const message = {
        type: constants.RTM_PROJECT_INGEST_DELETE,
        ingestionId,
      };

      const response = await fetchFromRTM(
        this.connection,
        message,
        constants.RTM_PROJECT_INGEST_DELETE_OK,
        constants.RTM_PROJECT_INGEST_DELETE_ERROR,
        ''
      );
      return response;
    };

    /**
     * See
     *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-ingestion-root-get/)
     * @param {string} payload.rootElementId
     * @param {string} payload.projectId
     * @returns {Promise.<void>}
     */
     projectIngestionRootGet = async ({ rootElementId, projectId }) => {
       const message = {
         type: constants.RTM_PROJECT_INGESTION_ROOT_GET,
         rootElementId,
         projectId,
       };

       const response = await fetchFromRTM(
         this.connection,
         message,
         constants.RTM_PROJECT_INGESTION_ROOT_GET_OK,
         constants.RTM_PROJECT_INGESTION_ROOT_GET_ERROR,
         '',
       );
       return response;
     };

     /**
     * See
     *   [RTM Docs](https://unsightly-rock.cloudvent.net/messages/project-ingest-get/)
     * @param {string} payload.ingestionId
     * @returns {Promise.<void>}
     */
     projectIngestGet = async ({ingestionId}) => {
       const message = {
         type: constants.RTM_PROJECT_INGEST_GET,
         ingestionId,
       };

       const response = await fetchFromRTM(
         this.connection,
         message,
         constants.RTM_PROJECT_INGEST_GET_OK,
         constants.RTM_PROJECT_INGEST_GET_ERROR,
         ''
       );

       return response;
     };
}

export default ProjectResolver;
